import Notification from '@rio-cloud/rio-uikit/components/notification/Notification';
import ConfirmationDialog from '@rio-cloud/rio-uikit/ConfirmationDialog';
import useHover from '@rio-cloud/rio-uikit/hooks/useHover';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';
import { KeyboardEventHandler, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';

import ActionIcon from '@/components/atom/ActionIcon/ActionIcon';
import { SCROLLBAR_CONTAINER } from '@/components/atom/ChatAssetTree/constants';
import {
  FavoriteIcon,
  highlightStyle,
  IconContainer,
  InputContainer,
  StyledInput,
  StyledItemAction,
  StyledThreadListItem,
  StyledThreadListItemManager,
} from '@/components/organism/ThreadListItem/ThreadListItem.styles';
import { getUserProfile } from '@/configuration/login/loginSlice';
import { useAppDispatch, useAppSelector } from '@/configuration/setup/hooks';
import { EResponseStatus } from '@/services/config/types';
import { threadApi, useDeleteThreadMutation, useUpdateThreadMutation } from '@/services/threadApi/threadApi';
import { disableOnboardingMode } from '@/store/onboarding/onboardingSlice';
import { closeSidebar, setScrollPosition } from '@/store/sidebar/sidebarSlice';
import { RootState } from '@/store/store';
import {
  resetActiveThread,
  selectActiveThreadId,
  selectAllThreadsLoading,
  selectThreadById,
  setActiveThread,
  toggleFavorite,
  updateThreadTitle,
} from '@/store/thread/threadSlice';
import { handleEnterKeyInteraction } from '@/utils/accessibilityHandlers/handleEnterKeyInteraction';
import { ONBOARDING_THREAD_ID } from '@/utils/constants';
import { EllipsisText } from '@/utils/EllipsisText';
import { getUserId } from '@/utils/getUserId';
import Logger from '@/utils/logger';

const texts = {
  removeFromFavorites: 'chatPage.chatThreadList.threadRemoveFromFavorites.label',
  addToFavorites: 'chatPage.chatThreadList.threadAddToFavorites.label',
  editThreadTitle: 'chatPage.chatThreadList.editThreadTitle.label',
  threadDeletion: 'chatPage.chatThreadList.threadDeletion.label',
  threadDeletionSuccess: 'chatPage.chatThreadList.threadDeletion.success.notification',
  removeFromFavoritesSuccess: 'chatPage.chatThreadList.threadRemoveFromFavorites.success.notification',
  threadDeletionError: 'chatPage.chatThreadList.threadDeletion.error',
  addToFavoritesSuccess: 'chatPage.chatThreadList.threadAddToFavorites.success.notification',
  removingFavoritesError: 'chatPage.chatThreadList.threadToggleFavorites.errorRemoving.notification',
  addingFavoritesError: 'chatPage.chatThreadList.threadToggleFavorites.errorAdding.notification',
  confirmDeletionTitle: 'chatPage.chatThreadList.confirmDeletion.title',
  confirmDeletionContent: 'chatPage.chatThreadList.confirmDeletion.content',
  cancel: 'common.cancel',
  confirm: 'common.confirm',
};

const classes = {
  editIcon: 'rioglyph rioglyph-pencil text-color-light text-size-18 hover-text-color-primary',
  favoriteIcon: 'rioglyph rioglyph-star margin-left-10 text-color-primary text-size-18',
  unfavoriteIcon: 'rioglyph rioglyph-star  margin-left-10 text-color-light text-size-18 hover-text-color-primary',
  deleteIcon: 'rioglyph rioglyph-trash text-color-light text-size-18 hover-text-color-primary',
  clearIcon: 'rioglyph rioglyph-remove-sign margin-left-5 text-color-primary text-size-18',
  inputTitle: 'width-100pct form-control bg-none text-size-14',
};

const LOGGER_TAG = 'ThreadListItem';

type ThreadListItemProps = {
  index: number;
  threadId: string;
  firstMessage: string;
  isFavorite: boolean;
  searchWord?: string;
};

const ThreadListItem = ({ index, threadId, firstMessage, isFavorite, searchWord }: ThreadListItemProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUserProfile);
  const userId = getUserId(user?.sub ?? '');
  const activeThreadId = useAppSelector(selectActiveThreadId);
  const thread = useAppSelector((state: RootState) => selectThreadById(state, threadId));
  const threadTitle = thread ? thread.title : '';
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [title, setTitle] = useState(threadTitle ?? firstMessage);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listItemRef = useRef<HTMLLIElement>(null);
  const isHovered = useHover(listItemRef);
  const isActive = activeThreadId === threadId;
  const isSmallScreen = useMediaQuery({ query: '(max-width: 47.5rem)' });
  const isMobileOrTablet = useMediaQuery({ maxWidth: '64rem' });

  const allThreadsLoading = useAppSelector(selectAllThreadsLoading);
  const isThreadLoading = threadId === null || allThreadsLoading.includes(threadId);

  const [deleteThread] = useDeleteThreadMutation();
  const [updateThreadTitleMutation] = useUpdateThreadMutation();
  const [toggleFavoriteMutation, { isLoading: isLoadingFavorite }] = useUpdateThreadMutation();

  const [fetchMessagesByThreadId] = threadApi.endpoints.fetchMessagesByThreadId.useLazyQuery();

  const TOOLTIP_THRESHOLD = 27;
  const TOOLTIP_WIDE_THRESHOLD = 40;
  const TOOLTIP_PLACEMENT = 'right';

  const handleConfirmDelete = async () => {
    try {
      if (threadId) {
        const response = await deleteThread({ userId, threadId }).unwrap();
        if (response.status === EResponseStatus.SUCCESS) {
          Notification.success(intl.formatMessage({ id: texts.threadDeletionSuccess }));
          dispatch(resetActiveThread());
        } else {
          Notification.error(intl.formatMessage({ id: texts.threadDeletionError }));
        }
      }
    } catch (error) {
      Notification.error(intl.formatMessage({ id: texts.threadDeletionError }));
      console.log(error);
    } finally {
      setShowDeleteDialog(false);
    }
  };

  const handleToggleFavorite = async () => {
    dispatch(setScrollPosition(document.querySelector(SCROLLBAR_CONTAINER)?.scrollTop || 0));
    try {
      if (threadId) {
        await toggleFavoriteMutation({
          userId,
          threadId,
          dataToUpdate: { favorite: !isFavorite },
        }).unwrap();
        dispatch(toggleFavorite(threadId));

        Notification.success(
          intl.formatMessage({
            id: !isFavorite ? texts.addToFavoritesSuccess : texts.removeFromFavoritesSuccess,
          }),
        );
      }
    } catch (error) {
      Notification.error(
        intl.formatMessage({ id: isFavorite ? texts.removingFavoritesError : texts.addingFavoritesError }),
      );
      Logger.error(LOGGER_TAG, 'Failed to update thread favorite status', error);
    }
  };

  const handleEditThreadTitle = () => setIsEditing(true);

  const handleUpdateThreadTitle = async () => {
    setIsEditing(false);
    if (firstMessage !== title && threadId) {
      try {
        await updateThreadTitleMutation({ userId, threadId, dataToUpdate: { title } }).unwrap();
        dispatch(updateThreadTitle({ title }));
      } catch (error) {
        Logger.error(LOGGER_TAG, 'Failed to update thread title', error);
        Notification.error(intl.formatMessage({ id: texts.threadDeletionError }));
      }
    }
  };

  const handleInputKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter' && isEditing) {
      handleUpdateThreadTitle();
    }
  };

  const handleClearTitle = () => {
    setTitle('');
    inputRef.current?.focus();
  };

  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setIsEditing(false);
        setTitle(firstMessage);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [firstMessage]);

  const handleIconClick = (e: MouseEvent<HTMLSpanElement>, handler: () => void) => {
    e.stopPropagation();
    handler();
  };

  const iconProps = [
    {
      onClick: (e: MouseEvent<HTMLSpanElement>) => handleIconClick(e, handleEditThreadTitle),
      iconClass: classes.editIcon,
      tooltipText: texts.editThreadTitle,
      dataTestId: 'edit-thread-title',
    },
    {
      onClick: (e: MouseEvent<HTMLSpanElement>) => handleIconClick(e, () => setShowDeleteDialog(true)),
      iconClass: classes.deleteIcon,
      tooltipText: texts.threadDeletion,
      dataTestId: 'delete-thread',
    },
  ];

  const handleOnClick = useCallback(async () => {
    const scrollContainer = document.querySelector(SCROLLBAR_CONTAINER);
    dispatch(setScrollPosition(scrollContainer?.scrollTop ?? 0));

    const isOnboarding = threadId === ONBOARDING_THREAD_ID;

    if (threadId === ONBOARDING_THREAD_ID) {
      dispatch(setActiveThread(threadId));
    } else if (activeThreadId !== threadId) {
      dispatch(resetActiveThread());
      dispatch(disableOnboardingMode());
      dispatch(setActiveThread(threadId));
    }

    if (threadId && !isOnboarding) {
      await fetchMessagesByThreadId({ userId, threadId }).unwrap();
    }

    if (isSmallScreen) {
      dispatch(closeSidebar());
    }
  }, [threadId, activeThreadId, userId, isSmallScreen, dispatch, fetchMessagesByThreadId]);

  const handleKeyDown = useMemo(
    () =>
      handleEnterKeyInteraction(handleOnClick, {
        disabled: isThreadLoading,
        preventDefault: true,
        triggerOnSpace: false,
      }),
    [handleOnClick, isThreadLoading],
  );

  return (
    <StyledThreadListItemManager key={`list-item-wrapper-${index}`}>
      {!isEditing ? (
        <OverlayTrigger
          placement={TOOLTIP_PLACEMENT}
          overlay={
            title?.length > TOOLTIP_THRESHOLD ? (
              <Tooltip id="tooltip" allowOnTouch width={title?.length > TOOLTIP_WIDE_THRESHOLD ? 300 : 'auto'}>
                {title}
              </Tooltip>
            ) : (
              <></>
            )
          }
        >
          <StyledThreadListItem
            ref={listItemRef}
            onClick={handleOnClick}
            $isactive={isActive}
            $search={searchWord}
            tabIndex={0}
            onKeyDown={(e) => handleKeyDown(e)}
          >
            {isThreadLoading && !isActive && (
              <div className="margin-right-10">
                <Spinner />
              </div>
            )}

            {!searchWord || searchWord.length === 0 ? (
              <EllipsisText>{title}</EllipsisText>
            ) : (
              <Highlighter
                highlightStyle={highlightStyle}
                searchWords={[searchWord]}
                autoEscape={false}
                textToHighlight={title}
              />
            )}
            <StyledItemAction $search={searchWord}>
              {(isHovered || searchWord || searchWord?.length !== 0 || isMobileOrTablet) && (
                <IconContainer>
                  {iconProps.map(({ onClick, dataTestId, iconClass, tooltipText }) => (
                    <div data-testid={dataTestId} key={tooltipText}>
                      <ActionIcon onClick={onClick} iconClass={iconClass} tooltipText={tooltipText} />
                    </div>
                  ))}
                </IconContainer>
              )}
              {isLoadingFavorite ? (
                <div className="margin-left-10">
                  <Spinner />
                </div>
              ) : (
                <FavoriteIcon
                  onClick={(e) => handleIconClick(e, handleToggleFavorite)}
                  iconClass={isFavorite ? classes.favoriteIcon : classes.unfavoriteIcon}
                  tooltipText={isFavorite ? texts.removeFromFavorites : texts.addToFavorites}
                  isFavorite={isFavorite}
                />
              )}
            </StyledItemAction>
          </StyledThreadListItem>
        </OverlayTrigger>
      ) : (
        <InputContainer>
          <StyledInput
            ref={inputRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={handleInputKeyDown}
            autoFocus
            className={classes.inputTitle}
            data-testid="edit-thread-title-input"
          />
          {title && <ActionIcon onClick={handleClearTitle} iconClass={classes.clearIcon} tooltipText={texts.cancel} />}
        </InputContainer>
      )}

      {showDeleteDialog && (
        <ConfirmationDialog
          show={showDeleteDialog}
          title={intl.formatMessage({ id: texts.confirmDeletionTitle })}
          content={intl.formatMessage({ id: texts.confirmDeletionContent })}
          onClickConfirm={handleConfirmDelete}
          onClickCancel={() => setShowDeleteDialog(false)}
          cancelButtonText={intl.formatMessage({ id: texts.cancel })}
          confirmButtonText={
            <>
              <span className="rioglyph rioglyph-ok margin-right-5" />
              <span>{intl.formatMessage({ id: texts.confirm })}</span>
            </>
          }
        />
      )}
    </StyledThreadListItemManager>
  );
};

export default ThreadListItem;
